import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getClientContractTermDetailLink } from '~/utils/getLinks';
import type { ClientContractListPresentation } from '../presentations';
import { useGetPartnerAdminsClientContracts } from './useGetPartnerAdminsClientContracts';

type PresentationProps = React.ComponentProps<typeof ClientContractListPresentation>;

type IUseClientContractList = () => {
  state: {
    /** API レスポンス全体（data.data） */
    data: ReturnType<typeof useGetPartnerAdminsClientContracts>['data'];
    /** 利用企業名 Input の現在値（制御コンポーネント用） */
    clientNameValue: string;
    /** 契約プラン名 Input の現在値（制御コンポーネント用） */
    contractPlanNameValue: string;
    /**
     * 検索ボタン押下済み（committedFilterParams に検索条件が存在する）かどうか
     *
     * 検索結果 0 件と全件 0 件（Empty 状態）を区別するために使用する
     */
    isFiltered: boolean;
    /** 現在のページ番号（1-indexed） */
    currentPage: number;
    /** 総ページ数 */
    totalPage: number;
    /** 総件数 */
    totalCount: number;
  };
  actions: {
    /** 利用企業名 Input の値変更時のハンドラ（API は発火させない） */
    handleClientNameChange: (value: string) => void;
    /** 契約プラン名 Input の値変更時のハンドラ（API は発火させない） */
    handleContractPlanNameChange: (value: string) => void;
    /** 検索ボタン押下時のハンドラ（committedFilterParams を更新して API を発火させる） */
    handleSearch: () => void;
    /** 利用企業名 Input の × ボタン押下時のハンドラ（client_name のみ削除して API を再発火させる） */
    handleClientNameClear: () => void;
    /** 契約プラン名 Input の × ボタン押下時のハンドラ（contract_plan_name のみ削除して API を再発火させる） */
    handleContractPlanNameClear: () => void;
    /** ページ切替ハンドラ */
    handlePageChange: PresentationProps['pagerProps']['onPageChange'];
    /**
     * 行クリック時のハンドラ（企業契約期間 詳細画面へ遷移）
     *
     * @param termId - 企業契約期間 ID
     * @param clientId - 利用企業 ID
     */
    handleRowClick: (termId: number, clientId: number) => void;
  };
};

/**
 * 企業契約期間一覧の状態を管理するフック
 *
 * - サイドバー導線のため clientId は不要（横断 API `/partner_admins/client_contracts` を使用）
 * - threeLetterCode は Cookie から取得する（partner-admin の標準パターン）
 * - 検索（client_name / contract_plan_name）・ページネーション（page）は URL クエリパラメータと双方向同期する
 * - committedFilterParams（useState）を SWR キーにすることで、
 *   検索ボタン押下・ページ変更時のみ API が再発火する
 */
export const useClientContractList: IUseClientContractList = () => {
  const threeLetterCode = getThreeLetterCode() ?? '';
  const navigate = useNavigate();

  const [filterParams, setFilterParams] = useSearchParams();

  // committedFilterParams（useState）: useGetPartnerAdminsClientContracts に渡す API 用パラメータ。
  // 検索ボタン押下・ページ変更時のみ更新することで SWR の再フェッチを制御する。
  const [committedFilterParams, setCommittedFilterParams] = useState(
    () => new URLSearchParams(filterParams)
  );

  // URL クエリパラメータから入力欄の表示値を導出する（制御コンポーネント用）
  const clientNameValue = filterParams.get('client_name') ?? '';
  const contractPlanNameValue = filterParams.get('contract_plan_name') ?? '';

  // committedFilterParams で判定する（入力途中の filterParams ではなく、
  // 実際に API が発火した検索条件があるかどうかを表す）
  const isFiltered =
    Boolean(committedFilterParams.get('client_name')) ||
    Boolean(committedFilterParams.get('contract_plan_name'));

  const currentPage = Number(committedFilterParams.get('page')) || 1;

  const { data, totalPage, totalCount } = useGetPartnerAdminsClientContracts({
    threeLetterCode,
    clientName: committedFilterParams.get('client_name') ?? undefined,
    contractPlanName: committedFilterParams.get('contract_plan_name') ?? undefined,
    page: committedFilterParams.get('page') ?? undefined,
  });

  /**
   * 利用企業名 Input 変更時のハンドラ
   *
   * URL の client_name パラメータを replace: true で更新する（API は発火させない）
   *
   * @param value 入力値
   */
  const handleClientNameChange = (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set('client_name', value);
    } else {
      newParams.delete('client_name');
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * 契約プラン名 Input 変更時のハンドラ
   *
   * URL の contract_plan_name パラメータを replace: true で更新する（API は発火させない）
   *
   * @param value 入力値
   */
  const handleContractPlanNameChange = (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set('contract_plan_name', value);
    } else {
      newParams.delete('contract_plan_name');
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * 検索ボタン押下時のハンドラ
   *
   * page を削除し、filterParams を committedFilterParams にコピーして API を発火させる
   */
  const handleSearch = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * 利用企業名 Input の × ボタン押下時のハンドラ
   *
   * client_name と page のみ削除し、committedFilterParams も同期して API を再発火させる
   * （contract_plan_name は維持する）
   */
  const handleClientNameClear = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('client_name');
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * 契約プラン名 Input の × ボタン押下時のハンドラ
   *
   * contract_plan_name と page のみ削除し、committedFilterParams も同期して API を再発火させる
   * （client_name は維持する）
   */
  const handleContractPlanNameClear = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('contract_plan_name');
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * ページ切替ハンドラ
   *
   * committedFilterParams ベースで page を更新して API を発火させる
   *
   * @param page 遷移先のページ番号（1-indexed）
   */
  const handlePageChange: PresentationProps['pagerProps']['onPageChange'] = (page: number) => {
    const newCommitted = new URLSearchParams(committedFilterParams);
    newCommitted.set('page', String(page));
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', String(page));
    setFilterParams(newParams);
    setCommittedFilterParams(newCommitted);
  };

  /**
   * 行クリック時のハンドラ（企業契約期間 詳細画面へ遷移）
   *
   * @param termId - 企業契約期間 ID
   * @param clientId - 利用企業 ID
   */
  const handleRowClick = (termId: number, clientId: number) => {
    navigate(getClientContractTermDetailLink({ clientId, termId }));
  };

  return {
    state: {
      data,
      clientNameValue,
      contractPlanNameValue,
      isFiltered,
      currentPage,
      totalPage,
      totalCount,
    },
    actions: {
      handleClientNameChange,
      handleContractPlanNameChange,
      handleSearch,
      handleClientNameClear,
      handleContractPlanNameClear,
      handlePageChange,
      handleRowClick,
    },
  };
};
